import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  Output,
  ViewChild,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { setShapeDescriptorAttribute } from '../../store/studio';
import { StudioImageShapeDescriptor, StudioImage } from '../../store/studio/studio.types';

type DragMode = 'move' | 'nw' | 'ne' | 'sw' | 'se';

interface CropRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

@Component({
  selector: 'nw-image-edit-modal',
  templateUrl: './image-edit-modal.component.html',
  styleUrls: ['./image-edit-modal.component.scss'],
})
export class ImageEditModalComponent implements OnChanges {
  @Input() shapeId: string;
  @Input() descriptor: StudioImageShapeDescriptor;
  @Input() image: StudioImage;
  @Output() closed = new EventEmitter<void>();

  @ViewChild('cropCanvas') cropCanvasRef: ElementRef<HTMLElement>;

  colorize: string | null = null;
  radius = 0;

  cropMode = false;
  cropRect: CropRect = { x: 0, y: 0, w: 1, h: 1 };

  private _dragState: {
    mode: DragMode;
    startX: number;
    startY: number;
    startRect: CropRect;
  } | null = null;

  get imageUrl(): string {
    if (!this.image) return '';
    return this.image.url || `https://private.cdn.inwedio.com/images/${this.image.id}`;
  }

  constructor(private store: Store) {}

  ngOnChanges() {
    this.colorize = this.descriptor?.colorize || null;
    this.radius = this.descriptor?.radius ?? 0;
  }

  onColorizeChanged(color: string) {
    this.colorize = color;
    this.store.dispatch(setShapeDescriptorAttribute({ key: 'colorize', value: color }));
  }

  clearColorize() {
    this.colorize = null;
    this.store.dispatch(setShapeDescriptorAttribute({ key: 'colorize', value: null }));
  }

  onRadiusChanged(value: number) {
    this.radius = value;
    this.store.dispatch(setShapeDescriptorAttribute({ key: 'radius', value }));
  }

  // --- Crop ---

  enterCropMode() {
    const c = this.descriptor?.crop;
    this.cropRect = c ? { ...c } : { x: 0, y: 0, w: 1, h: 1 };
    this.cropMode = true;
  }

  saveCrop() {
    this.store.dispatch(setShapeDescriptorAttribute({ key: 'crop', value: { ...this.cropRect } }));
    this.cropMode = false;
  }

  discardCrop() {
    this.cropMode = false;
  }

  onCropMouseDown(event: MouseEvent, mode: DragMode) {
    event.stopPropagation();
    this._dragState = {
      mode,
      startX: event.clientX,
      startY: event.clientY,
      startRect: { ...this.cropRect },
    };
  }

  @HostListener('document:mousemove', ['$event'])
  onMouseMove(event: MouseEvent) {
    if (!this._dragState || !this.cropCanvasRef) return;
    const { width, height } = this.cropCanvasRef.nativeElement.getBoundingClientRect();
    const dx = (event.clientX - this._dragState.startX) / width;
    const dy = (event.clientY - this._dragState.startY) / height;
    const { mode, startRect: s } = this._dragState;
    const MIN = 0.05;

    switch (mode) {
      case 'move':
        this.cropRect = {
          ...this.cropRect,
          x: Math.max(0, Math.min(1 - s.w, s.x + dx)),
          y: Math.max(0, Math.min(1 - s.h, s.y + dy)),
        };
        break;
      case 'nw': {
        const nx = Math.max(0, Math.min(s.x + s.w - MIN, s.x + dx));
        const ny = Math.max(0, Math.min(s.y + s.h - MIN, s.y + dy));
        this.cropRect = { x: nx, y: ny, w: s.x + s.w - nx, h: s.y + s.h - ny };
        break;
      }
      case 'ne': {
        const ny = Math.max(0, Math.min(s.y + s.h - MIN, s.y + dy));
        this.cropRect = {
          ...this.cropRect,
          y: ny,
          w: Math.max(MIN, Math.min(1 - s.x, s.w + dx)),
          h: s.y + s.h - ny,
        };
        break;
      }
      case 'sw': {
        const nx = Math.max(0, Math.min(s.x + s.w - MIN, s.x + dx));
        this.cropRect = {
          ...this.cropRect,
          x: nx,
          w: s.x + s.w - nx,
          h: Math.max(MIN, Math.min(1 - s.y, s.h + dy)),
        };
        break;
      }
      case 'se':
        this.cropRect = {
          ...this.cropRect,
          w: Math.max(MIN, Math.min(1 - s.x, s.w + dx)),
          h: Math.max(MIN, Math.min(1 - s.y, s.h + dy)),
        };
        break;
    }
  }

  @HostListener('document:mouseup')
  onMouseUp() {
    this._dragState = null;
  }
}
